import { useState, useEffect } from 'react';
import MediaViewer from '../../components/admin/MediaViewer';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search,
  MoreVert,
  Visibility,
  ExpandMore,
  FilterList,
  Task,
  LocationOn,
  Person,
  Schedule,
  Payment,
  PhotoLibrary,
  VideoLibrary 
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';

export default function TaskModeration() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all'
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState([]);
  const [currentSubmission, setCurrentSubmission] = useState(null);

  // Debug logging
  useEffect(() => {
    console.log('TaskModeration component mounted');
    return () => console.log('TaskModeration component unmounted');
  }, []);

  useEffect(() => {
    console.log('Tasks state updated:', tasks.length, 'tasks');
  }, [tasks]);

  useEffect(() => {
    console.log('Task details updated:', taskDetails);
  }, [taskDetails]);

  useEffect(() => {
    console.log('Current media updated:', currentMedia);
  }, [currentMedia]);

  // Fetch tasks data
  const fetchTasks = async (page = 0, search = '', filters = {}) => {
    try {
      console.log('Fetching tasks:', { page, search, filters });
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: pagination.limit.toString(),
        search: search,
        status: filters.status || 'all',
        category: filters.category || 'all'
      });

      console.log('API URL:', `https://propertyearn.net/api/api/admin/tasks?${params}`);

      const response = await fetch(
        `https://propertyearn.net/api/api/admin/tasks?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Tasks response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      console.log('Tasks data received:', data);
      setTasks(data.tasks);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch task details
  const fetchTaskDetails = async (taskId) => {
    try {
      console.log('Fetching task details for:', taskId);
      setActionLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `https://propertyearn.net/api/api/admin/tasks/${taskId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Task details response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch task details');
      }

      const data = await response.json();
      console.log('Task details received:', data);
      
      // Debug: Check if submissions have media_files
      if (data.submissions && data.submissions.length > 0) {
        console.log('Submissions with media info:');
        data.submissions.forEach((sub, index) => {
          console.log(`Submission ${index}:`, {
            id: sub.id,
            hasMediaFiles: !!sub.media_files,
            mediaFilesCount: sub.media_files ? sub.media_files.length : 0,
            mediaFiles: sub.media_files
          });
        });
      }
      
      setTaskDetails(data);
      setDetailsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update task status
  const updateTaskStatus = async (taskId, newStatus, reason = '') => {
    try {
      console.log('Updating task status:', { taskId, newStatus, reason });
      setActionLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `https://propertyearn.net/api/api/admin/tasks/${taskId}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus, reason })
        }
      );

      console.log('Status update response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update task status');
      }

      alert('Task status updated successfully!');
      fetchTasks(pagination.page, searchTerm, filters);
      setDetailsDialogOpen(false);
    } catch (err) {
      console.error('Error updating task status:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'primary';
      case 'Completed': return 'success';
      case 'Rejected': return 'error';
      case 'Pending Review': return 'warning';
      case 'In Progress': return 'info';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Photography': return 'primary';
      case 'Verification': return 'secondary';
      case 'Research': return 'info';
      case 'Survey': return 'warning';
      case 'Virtual Tour': return 'success';
      default: return 'default';
    }
  };


  const handleViewMedia = async (submission) => {
  try {
    console.log('Viewing media for submission:', submission.id);
    setActionLoading(true);
    
    // Set current submission info immediately
    setCurrentSubmission({
      id: submission.id,
      submitterName: submission.submitter.name,
      status: submission.status,
      submittedAt: submission.submitted_at
    });
    
    // First check if we already have media files in the submission data
    if (submission.media_files && submission.media_files.length > 0) {
      console.log('Using existing media files from submission:', submission.media_files);
      setCurrentMedia(submission.media_files);
      setMediaDialogOpen(true);
      return;
    }
    
    // If no media files in submission data, fetch them from the API
    console.log('No media files in submission data, fetching from API...');
    const token = localStorage.getItem('adminToken');
    const response = await fetch(
      `https://propertyearn.net/api/api/admin/tasks/${taskDetails.id}/submissions/${submission.id}/media`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    console.log('Media fetch response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('Media data received from API:', data);
      
      if (data.media && data.media.length > 0) {
        setCurrentMedia(data.media);
      } else {
        setCurrentMedia([]);
        console.log('No media files found for this submission');
      }
    } else {
      console.warn('Failed to fetch media files from API');
      setCurrentMedia([]);
    }
    
    setMediaDialogOpen(true);
    
  } catch (err) {
    console.error('Error in handleViewMedia:', err);
    setError('Failed to load media files');
    setCurrentMedia([]);
    setMediaDialogOpen(true); // Still open dialog to show error state
  } finally {
    setActionLoading(false);
  }
};


// Function to handle media errors gracefully
const handleMediaError = (mediaFile) => {
  console.error(`Failed to load media: ${mediaFile.url}`);
  // You could set a state to show broken image placeholders
};

  if (loading) {
    return (
      <AdminLayout pageTitle="Task Moderation">
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Task Moderation">
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Published">Published</MenuItem>
                  <MenuItem value="Pending Review">Pending Review</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  label="Category"
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Photography">Photography</MenuItem>
                  <MenuItem value="Verification">Verification</MenuItem>
                  <MenuItem value="Research">Research</MenuItem>
                  <MenuItem value="Survey">Survey</MenuItem>
                  <MenuItem value="Virtual Tour">Virtual Tour</MenuItem>
                  <MenuItem value="Data Entry">Data Entry</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => fetchTasks(0, searchTerm, filters)}
                startIcon={<FilterList />}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tasks Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reward</TableCell>
                  <TableCell>Poster</TableCell>
                  <TableCell>Submissions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id} hover>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="bold">{task.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {task.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.category} 
                        color={getCategoryColor(task.category)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.status} 
                        color={getStatusColor(task.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {task.currency}{task.reward.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography>{task.poster.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ⭐ {task.poster.rating}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.submission_count} 
                        variant="outlined" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedTask(task);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={(e, newPage) => fetchTasks(newPage, searchTerm, filters)}
            onRowsPerPageChange={(e) => {
              setPagination({ ...pagination, limit: parseInt(e.target.value) });
              fetchTasks(0, searchTerm, filters);
            }}
          />
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => {
            fetchTaskDetails(selectedTask.id);
            setAnchorEl(null);
          }}>
            <Visibility sx={{ mr: 1 }} /> View Details
          </MenuItem>
        </Menu>

        {/* Task Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Task Details: {taskDetails?.title}</DialogTitle>
          <DialogContent>
            {taskDetails && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Basic Information</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Title:</strong></Typography>
                          <Typography sx={{ ml: 1 }}>{taskDetails.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Category:</strong></Typography>
                          <Typography sx={{ ml: 1 }}>{taskDetails.category}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Status:</strong></Typography>
                          <Chip 
                            label={taskDetails.status} 
                            color={getStatusColor(taskDetails.status)} 
                            size="small" 
                            sx={{ ml: 1 }} 
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Reward:</strong></Typography>
                          <Typography sx={{ ml: 1 }}>
                            {taskDetails.currency}{taskDetails.reward.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Deadline:</strong></Typography>
                          <Typography sx={{ ml: 1 }}>
                            {taskDetails.deadline ? new Date(taskDetails.deadline).toLocaleDateString() : 'None'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>People</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography><strong>Posted by:</strong></Typography>
                          <Typography sx={{ ml: 1 }}>
                            {taskDetails.poster.name} (⭐ {taskDetails.poster.rating})
                          </Typography>
                        </Box>
                        {taskDetails.assignee && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography><strong>Assigned to:</strong></Typography>
                            <Typography sx={{ ml: 1 }}>{taskDetails.assignee.name}</Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Description</Typography>
                        <Typography>{taskDetails.description}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {taskDetails.location && taskDetails.location.address && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Location
                          </Typography>
                          <Typography>{taskDetails.location.address}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {taskDetails.requirements && taskDetails.requirements.length > 0 && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Requirements</Typography>
                          {taskDetails.requirements.map((req, index) => (
                            <Typography key={index}>• {req.requirement_text}</Typography>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                    {taskDetails.submissions && taskDetails.submissions.length > 0 && (
                    <Grid item xs={12}>
                        <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                            Submissions ({taskDetails.submissions.length})
                            </Typography>
                            {taskDetails.submissions.map((submission) => {
                            const hasMediaFiles = submission.media_files && submission.media_files.length > 0;
                            console.log(`Submission ${submission.id} media:`, {
                                hasMediaFiles,
                                count: hasMediaFiles ? submission.media_files.length : 0,
                                files: hasMediaFiles ? submission.media_files : null
                            });
                            
                            return (
                                <Accordion key={submission.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography>
                                    {submission.submitter.name} - {new Date(submission.submitted_at).toLocaleDateString()}
                                    {hasMediaFiles && ` (${submission.media_files.length} media files)`}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography><strong>Status:</strong></Typography>
                                    <Typography sx={{ ml: 1 }}>{submission.status}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography><strong>Proof:</strong></Typography>
                                    <Typography sx={{ ml: 1 }}>{submission.proof_text}</Typography>
                                    </Box>
                                    
                                    {/* Media Section */}
                                    <Box sx={{ mt: 2 }}>
  <Typography variant="subtitle2" gutterBottom>
    Media Files: {submission.media_files ? submission.media_files.length : 0}
  </Typography>
  
  {submission.media_files && submission.media_files.length > 0 ? (
    <Box>
      {/* Media Thumbnails */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {submission.media_files.slice(0, 4).map((media, index) => (
          <Box
            key={media.id || index}
            sx={{
              width: 80,
              height: 80,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => handleViewMedia(submission)}
            title={`Click to view ${media.filename || 'media'}`}
          >
            {media.type === 'image' ? (
              <img
                src={media.url}
                alt={media.filename || `Submission media ${index + 1}`}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  backgroundColor: '#f5f5f5'
                }}
                onError={(e) => {
                  console.error('Image failed to load:', media.url);
                  // Show fallback icon
                  e.target.style.display = 'none';
                }}
              />
            ) : media.type === 'video' ? (
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <VideoLibrary />
              </Box>
            ) : (
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                bgcolor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PhotoLibrary sx={{ color: 'grey.600' }} />
              </Box>
            )}
            
            {/* Fallback for broken images */}
            {media.type === 'image' && (
              <Box 
                sx={{ 
                  display: 'none', // Hidden by default, shown on error
                  width: '100%', 
                  height: '100%', 
                  bgcolor: 'grey.100',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
                className="media-fallback"
              >
                <PhotoLibrary sx={{ color: 'grey.400' }} />
              </Box>
            )}
          </Box>
        ))}
        
        {/* Show "more" indicator if there are more than 4 files */}
        {submission.media_files.length > 4 && (
          <Box
            sx={{
              width: 80,
              height: 80,
              border: '1px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.50',
              cursor: 'pointer'
            }}
            onClick={() => handleViewMedia(submission)}
          >
            <Typography variant="body2" color="primary.main">
              +{submission.media_files.length - 4} more
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* View All Media Button */}
      <Button
        size="small"
        startIcon={<PhotoLibrary />}
        onClick={() => handleViewMedia(submission)}
        sx={{ mt: 1 }}
        variant="contained"
        color="primary"
      >
        View All Media ({submission.media_files.length})
      </Button>
    </Box>
  ) : (
    /* No Media Files State */
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1, 
      mt: 1,
      p: 2,
      bgcolor: 'grey.50',
      borderRadius: 1
    }}>
      <PhotoLibrary sx={{ color: 'grey.400' }} />
      <Typography variant="body2" color="text.secondary">
        No media files uploaded for this submission


      </Typography>
    </Box>
  )}
                                    </Box>


                                </AccordionDetails>
                                </Accordion>
                            );
                            })}
                        </CardContent>
                        </Card>
                    </Grid>
                    )}
                </Grid>

                {/* Status Actions */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>Moderation Actions</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {/* APPROVE BUTTON - Changed from 'Published' to 'Completed' */}
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => updateTaskStatus(taskDetails.id, 'Completed')}
                        disabled={actionLoading}
                      >
                        Approve & Complete
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => updateTaskStatus(taskDetails.id, 'Rejected')}
                        disabled={actionLoading}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => updateTaskStatus(taskDetails.id, 'Pending Review')}
                        disabled={actionLoading}
                      >
                        Mark for Review
                      </Button>
                    </Box>
                  </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Media Viewer Dialog */}
        <MediaViewer
          open={mediaDialogOpen}
          onClose={() => {
            setMediaDialogOpen(false);
            setCurrentMedia([]);
            setCurrentSubmission(null);
          }}
          mediaFiles={currentMedia}
          submissionInfo={currentSubmission}
        />
      </Box>
    </AdminLayout>
  );
}